import React, { useState, useEffect, useRef } from "react";
import "./EmailAiPopup.css";
import "./EmailAiModes/BulkModeEmail.css";
import "./EmailAiModes/AutoSendModeEmail.css";
import { ref, onValue, get, set } from 'firebase/database';
import { database, firestore } from '../firebaseConfig';
import { collection, onSnapshot, query, orderBy, doc, getDoc, addDoc, getDocs } from 'firebase/firestore';
import NormalModeEmail from './EmailAiModes/NormalModeEmail';
import InboxModeEmail from './EmailAiModes/InboxModeEmail';
import BulkModeEmail from './EmailAiModes/BulkModeEmail';
import AutoSendModeEmail from './EmailAiModes/AutoSendModeEmail';
import { standardizeInstagramProfile, isRecentlyAdded } from '../utils/InstagramHelper';

const EmailAiPopup = ({ 
  onClose, 
  rowData, 
  userId, 
  isReply = false, 
  source = "", 
  onEmailResult,
  bulkMode = false,
  autoSendMode = false,
  bulkContacts = [],
  allData = [],
  presetEmails = null
}) => {
  const [userSettings, setUserSettings] = useState({});
  const [aiSettings, setAiSettings] = useState({});
  const [userTopics, setUserTopics] = useState({});
  const [isTopicsLoaded, setIsTopicsLoaded] = useState(false);
  const [senderEmails, setSenderEmails] = useState([]);
  const [isAutoSendProcessing, setIsAutoSendProcessing] = useState(false);
  const [whisperSettings, setWhisperSettings] = useState({
    smartAddEnabled: false,
    profileNumber: 0
  });
  const [selectedTopic, setSelectedTopic] = useState(null);
  const hasAutoGenerated = React.useRef(false);

  // Bestimme den Modus basierend auf den Props
  const getMode = () => {
    if (autoSendMode) return "auto";
    if (bulkMode) return "bulk";
    if (isReply || source === "inbox") return "inbox";
    return "normal";
  };

  const mode = getMode();

  // Verhindere Scrollen im Hintergrund, ohne die Scroll-Position zu verlieren
  const scrollPosRef = useRef(0);
  useEffect(() => {
    scrollPosRef.current = window.scrollY || document.documentElement.scrollTop;

    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollPosRef.current}px`;
    document.body.classList.add('email-ai-popup-open');

    return () => {
      document.body.classList.remove('email-ai-popup-open');
      document.body.style.position = '';
      document.body.style.top = '';
      window.scrollTo(0, scrollPosRef.current);
    };
  }, []);

  // Lade die Benutzereinstellungen
  useEffect(() => {
    if (!userId) return;
    
    // Lade E-Mail-Einstellungen
    const settingsRef = ref(database, `/World/${userId}/settings/email`);
    const unsubSettings = onValue(settingsRef, (snapshot) => {
      if (snapshot.exists()) {
        // MERGE (never replace): other listeners add derived keys like
        // imageLabAssets / footer / calendly onto userSettings. A full replace
        // here races with them and wipes imageLabAssets, which makes the inline
        // {{image:token}} editor lose its asset map (renders raw braces).
        setUserSettings(prev => ({ ...prev, ...snapshot.val() }));
      }
    });
    
    // Lade AI-Einstellungen
    const aiSettingsRef = ref(database, `/World/${userId}/settings/ai`);
    const unsubAiSettings = onValue(aiSettingsRef, (snapshot) => {
      if (snapshot.exists()) {
        const loadedAiSettings = snapshot.val();
        setAiSettings(loadedAiSettings);
      }
    });

    // Lade Footer-Einstellungen
    const footerSettingsRef = ref(database, `/World/${userId}/settings/footer`);
    const unsubFooter = onValue(footerSettingsRef, (snapshot) => {
      if (snapshot.exists()) {
        const footerSettings = snapshot.val();
        
        // Prüfe ob Multi-Footer-System aktiv ist
        const useMultipleFooters = footerSettings.useMultipleFooters === true;
        const footerList = Array.isArray(footerSettings.footerList) ? footerSettings.footerList : [];
        
        if (useMultipleFooters && footerList.length > 0) {
          const firstActive = footerList.find(f => f && f.isActive !== false) || footerList[0];
          setUserSettings(prev => ({
            ...prev,
            useMultipleFooters: true,
            footerList: footerList,
            footer_font: firstActive?.footer_font || "Helvetica Neue"
          }));
        } else {
          // Legacy Single-Footer-System: Lade alte Einstellungen
          setUserSettings(prev => ({
            ...prev,
            footer_text: footerSettings.footer_text || "",
            footer_image_url: footerSettings.footer_image_url || "",
            footer_image_position: footerSettings.footer_image_position || "below",
            footer_font: footerSettings.footer_font || "Helvetica Neue",
            footer_image_size: footerSettings.footer_image_size || 80,
            socialIcons: footerSettings.socialIcons || [
              { iconUrl: "", link: "", size: 32 },
              { iconUrl: "", link: "", size: 32 },
              { iconUrl: "", link: "", size: 32 },
              { iconUrl: "", link: "", size: 32 }
            ],
            socialIconsPosition: footerSettings.socialIconsPosition || "left",
            socialIconsMarginTop: footerSettings.socialIconsMarginTop !== undefined ? footerSettings.socialIconsMarginTop : 15,
            socialIconsMarginBottom: footerSettings.socialIconsMarginBottom !== undefined ? footerSettings.socialIconsMarginBottom : 10,
            useMultipleFooters: false,
            footerList: []
          }));
        }
      }
    });

    // Lade Image-Lab-Assets (statische/dynamische Body-Bilder). Als
    // token->asset Map in userSettings gemerged, damit convertToHtml die
    // {{image:token}}-Platzhalter in Preview UND Send auflösen kann. Der Send
    // erhält die Map automatisch, weil resolveFooterForSend userSettings als
    // Fallback nutzt und Nicht-Footer-Keys unverändert durchreicht.
    const imageLabRef = ref(database, `/World/${userId}/settings/imageLab`);
    const unsubImageLab = onValue(imageLabRef, (snapshot) => {
      const node = snapshot.exists() ? snapshot.val() : null;
      const assetMap = {};
      if (node && typeof node === 'object') {
        Object.values(node).forEach((asset) => {
          // Include static assets (with imageUrl) AND dynamic AI assets (type:'ai',
          // no imageUrl yet — rendered per recipient and overridden at send time).
          if (asset && typeof asset === 'object' && asset.token && (asset.imageUrl || asset.type === 'ai' || asset.type === 'miniApp')) {
            assetMap[String(asset.token)] = asset;
          }
        });
      }
      setUserSettings(prev => ({ ...prev, imageLabAssets: assetMap }));
    });

    // Lade More-Einstellungen (für Calendly)
    const moreSettingsRef = ref(database, `/World/${userId}/settings/moreSettings`);
    const unsubMore = onValue(moreSettingsRef, (snapshot) => {
      if (snapshot.exists()) {
        const moreSettings = snapshot.val();
        setUserSettings(prev => ({
          ...prev,
          calendlyBookingLink: moreSettings.calendlyBookingLink
        }));
      }
    });

    // Lade Absender-E-Mails aus Firestore
    const emailAccountsCollectionRef = collection(firestore, 'world_users', userId, 'emailAccounts');
    const emailAccountsQuery = query(emailAccountsCollectionRef, orderBy('email'));
    
    const unsubscribeEmailAccounts = onSnapshot(emailAccountsQuery, (querySnapshot) => {
      const emails = [];
      querySnapshot.forEach((doc) => {
        emails.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      if (emails.length > 0) {
        setSenderEmails(emails);
      } else if (userSettings.email_username) {
        // Fallback auf die Einstellungen aus der Realtime Database (Legacy)
        setSenderEmails([{
          id: 'default',
          email: userSettings.email_username,
          password: userSettings.email_password,
          sender_name: userSettings.user_name || '',
          imap_host: userSettings.imap_host || 'imap.hostinger.com',
          imap_port: userSettings.imap_port || '993',
          encryption_type: userSettings.encryption_type || 'SSL',
          smtp_host: userSettings.smtp_host || 'smtp.hostinger.com',
          smtp_port: userSettings.smtp_port || '465',
          smtp_encryption: userSettings.smtp_encryption || 'true'
        }]);
      }
    }, (error) => {
      console.error("Fehler beim Laden der E-Mail-Accounts aus Firestore:", error);
      // Fallback auf Legacy-Daten bei Fehler
      if (userSettings.email_username) {
        setSenderEmails([{
          id: 'default',
          email: userSettings.email_username,
          password: userSettings.email_password,
          sender_name: userSettings.user_name || '',
          imap_host: userSettings.imap_host || 'imap.hostinger.com',
          imap_port: userSettings.imap_port || '993',
          encryption_type: userSettings.encryption_type || 'SSL',
          smtp_host: userSettings.smtp_host || 'smtp.hostinger.com',
          smtp_port: userSettings.smtp_port || '465',
          smtp_encryption: userSettings.smtp_encryption || 'true'
        }]);
      }
    });

    // Lade Whisper-Einstellungen aus Firestore
    const loadWhisperSettings = async () => {
      try {
        const settingsRef = doc(firestore, `Whisper/${userId}/settings`, 'general');
        const snapshot = await getDoc(settingsRef);
        
        if (snapshot.exists()) {
          const settings = snapshot.data();
          setWhisperSettings({
            smartAddEnabled: settings.smartAddEnabled || false,
            profileNumber: settings.profileNumber || 0
          });
          console.log('📱 [WHISPER] Settings loaded from Firestore:', settings);
        } else {
          console.log('📱 [WHISPER] No settings found, using defaults');
          setWhisperSettings({
            smartAddEnabled: false,
            profileNumber: 0
          });
        }
      } catch (error) {
        console.error('📱 [WHISPER] Error loading settings from Firestore:', error);
        setWhisperSettings({
          smartAddEnabled: false,
          profileNumber: 0
        });
      }
    };
    
    loadWhisperSettings();

    // Versuche, unvollständige Status-Updates zu finden und zu verarbeiten
    const processIncompleteStatusUpdates = async () => {
      try {
        const statusUpdatesRef = ref(database, `/World/${userId}/statusUpdates`);
        const snapshot = await get(statusUpdatesRef);
        
        if (snapshot.exists()) {
          const updates = snapshot.val();
          
          // Gehe alle Status-Updates durch
          for (const [leadId, updateData] of Object.entries(updates)) {
            // Suche nach Updates mit Status "queued" oder "error", die älter als 5 Minuten sind
            if ((updateData.status === 'queued' || updateData.status === 'error') && 
                updateData.timestamp && 
                Date.now() - updateData.timestamp > 5 * 60 * 1000) {
              
              console.log(`Verarbeite unvollständiges Status-Update für Lead ${leadId}: ${updateData.newStatus}`);
            }
          }
        }
      } catch (error) {
        console.error("Fehler beim Suchen nach unvollständigen Status-Updates:", error);
      }
    };
    
    processIncompleteStatusUpdates();
    
    // Cleanup-Funktion
    return () => {
      unsubSettings();
      unsubAiSettings();
      unsubFooter();
      unsubImageLab();
      unsubMore();
      unsubscribeEmailAccounts();
    };
  }, [userId, userSettings.email_username]);

  // Lade alle Topic-Daten
  useEffect(() => {
    if (!userId) return;
    
    let isMounted = true;
    setIsTopicsLoaded(false);
    
    const loadAllTopicData = async () => {
      try {
        // Referenzen erstellen
        const selectedTopicsRef = ref(database, `/World/${userId}/settings/selectedTopics`);
        const customTopicsRef = ref(database, `/World/${userId}/settings/customTopics`);
        
        // Daten parallel laden
        const [selectedSnapshot, customSnapshot] = await Promise.all([
          new Promise(resolve => onValue(selectedTopicsRef, snapshot => resolve(snapshot), { onlyOnce: true })),
          new Promise(resolve => onValue(customTopicsRef, snapshot => resolve(snapshot), { onlyOnce: true }))
        ]);
        
        if (!isMounted) return;
        
        // Kombiniere alle Topics in einem Objekt
        const allTopics = {};
        
        if (selectedSnapshot.exists()) {
          Object.assign(allTopics, selectedSnapshot.val());
        }
        
        if (customSnapshot.exists()) {
          Object.assign(allTopics, customSnapshot.val());
        }
        
        // Setze den State einmal mit allen Daten
        setUserTopics(allTopics);
        setIsTopicsLoaded(true);
        
      } catch (error) {
        console.error("Fehler beim Laden der Topic-Daten:", error);
        if (isMounted) {
          setIsTopicsLoaded(true); // Trotz Fehler als geladen markieren
        }
      }
    };
    
    // Lade initial alle Daten
    loadAllTopicData();
    
    // Richte Live-Updates ein
    const selectedTopicsRef = ref(database, `/World/${userId}/settings/selectedTopics`);
    const unsubscribe = onValue(selectedTopicsRef, (snapshot) => {
      if (!isMounted) return;
      
      if (snapshot.exists()) {
        // Aktualisiere nur die ausgewählten Topics, behalte benutzerdefinierte bei
        setUserTopics(prev => ({
          ...prev,
          ...snapshot.val()
        }));
      }
    });
    
    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, [userId]);

  // Setze das vorausgewählte Topic, wenn vorhanden
  useEffect(() => {
    if (rowData.selectedTopic) {
      setSelectedTopic(rowData.selectedTopic);
      hasAutoGenerated.current = true; // Verhindere automatische Generierung
    }
  }, [rowData.selectedTopic]);

  // Topic-Mapping: hardcoded defaults + dynamisch aus userTopics (Custom Topics haben type hinterlegt)
  const topicTypeMapping = React.useMemo(() => {
    const base = {
      "Introduce Yourself": "intro",
      "Follow Up": "follow_up_1",
      "Second Follow Up": "follow_up_2",
      "Third Follow Up": "follow_up_3",
      "Reply": "custom_reply"
    };
    if (userTopics) {
      Object.values(userTopics).forEach(t => {
        if (t.topic && t.type && !base[t.topic]) {
          base[t.topic] = t.type;
        }
      });
    }
    return base;
  }, [userTopics]);

  // Funktion zum Hinzufügen eines Instagram-Profils zur Whisper-DB
  const addInstagramProfileToWhisper = async (instagramProfile, contact = null) => {
    // Prüfe, ob Smart Send aktiviert ist
    if (!whisperSettings.smartAddEnabled) {
      console.log('📱 [WHISPER] Smart Add disabled, skipping Instagram profile add');
      return;
    }
    
    // Prüfen, ob ein Instagram-Profil vorhanden ist
    if (!instagramProfile) return;
    
    try {
      // Verwende die standardisierte Funktion, um das Instagram-Profil zu normalisieren
      const standardized = standardizeInstagramProfile(instagramProfile);
      
      // Wenn kein gültiger Profilname vorhanden ist, beende die Funktion
      if (!standardized.username) return;
      
      // Prüfe, ob das Profil kürzlich hinzugefügt wurde (zur Vermeidung von Mehrfachhinzufügungen)
      if (isRecentlyAdded(standardized.username)) {
        console.log(`📱 [WHISPER] Instagram-Profil ${standardized.username} wurde kürzlich bereits hinzugefügt`);
        return;
      }
      
      // Prüfe, ob das Profil bereits in Firestore existiert
      const profilesRef = collection(firestore, `Whisper/${userId}/profiles`);
      const existingProfilesSnapshot = await getDocs(profilesRef);
      
      let profileExists = false;
      
      existingProfilesSnapshot.forEach((doc) => {
        const profile = doc.data();
        if (profile.instagram_profile === standardized.username) {
          profileExists = true;
        }
      });
      
      // Wenn das Profil bereits existiert, beende die Funktion
      if (profileExists) {
        console.log(`📱 [WHISPER] Instagram-Profil ${standardized.username} existiert bereits in der Whisper-DB`);
        return;
      }
      
      // Erstelle einen neuen Eintrag in der WhisperDb
      const now = new Date().toISOString();
      
      // Neuer Eintrag für die Whisper-DB
      const newProfileEntry = {
        instagram_profile: standardized.username,
        profile_url: standardized.profile_url,
        status: "fresh",
        import_date: now,
        accountNumber: whisperSettings.profileNumber,
        source: "email_ai",
        raw_value: instagramProfile, // Speichere den ursprünglichen Wert
      };
      
      // Wenn Kontaktdaten vorhanden sind, füge weitere Informationen hinzu
      if (contact) {
        if (contact.lead_name) newProfileEntry.lead_name = contact.lead_name;
        if (contact.lead_email) newProfileEntry.lead_email = contact.lead_email;
        if (contact.lead_phone) newProfileEntry.lead_phone = contact.lead_phone;
        if (contact.lead_business) newProfileEntry.lead_business = contact.lead_business;
        if (contact.business_name) newProfileEntry.business_name = contact.business_name;
      }
      
      // Speichere den Eintrag in Firestore
      const docRef = await addDoc(profilesRef, newProfileEntry);
      
      console.log(`📱 [WHISPER] Instagram-Profil ${standardized.username} zur Whisper-DB hinzugefügt (ID: ${docRef.id})`);
    } catch (error) {
      console.error("📱 [WHISPER] Fehler beim Hinzufügen des Instagram-Profils zur Whisper-DB:", error);
    }
  };

  // Gemeinsame Props für alle Modi
  const commonProps = {
    onClose,
    rowData,
    userId,
    isReply,
    source,
    onEmailResult: async (status, message, contact = null, scheduledTime = null, recipientEmail = null, senderEmail = null) => {
      if (onEmailResult) {
        await onEmailResult(status, message, contact, scheduledTime, recipientEmail, senderEmail);
      }
      
      const instagramProfile = contact?.lead_instagram || rowData?.lead_instagram;
      if (instagramProfile) {
        await addInstagramProfileToWhisper(instagramProfile, contact || rowData);
      }
    },
    userSettings,
    aiSettings,
    userTopics,
    isTopicsLoaded,
    senderEmails,
    topicTypeMapping
  };

  // Rückverfolgung während des Ladens
  if (!isTopicsLoaded) {
    return (
      <div className="emailAiPopupOverlay">
        <div className="emailAiPopupContainer loading">
          <div className="emailAiPopupHeader">
            <h2>E-Mail wird geladen...</h2>
            <button className="emailAiPopupCloseButton" onClick={onClose}>×</button>
          </div>
          <div className="emailAiPopupContent">
            <div className="emailAiPopupLoadingSpinner"></div>
          </div>
        </div>
      </div>
    );
  }

  // Verschiedene Modi rendern
  const renderModeComponent = () => {
    switch (mode) {
      case "auto":
        return (
          <AutoSendModeEmail 
            {...commonProps}
            bulkContacts={bulkContacts}
            allData={allData}
            onProcessingChange={setIsAutoSendProcessing}
          />
        );
      case "bulk":
        return (
          <BulkModeEmail 
            {...commonProps}
            bulkContacts={bulkContacts}
            allData={allData}
            presetEmails={presetEmails}
          />
        );
      case "inbox":
        return (
          <InboxModeEmail 
            {...commonProps} 
          />
        );
      default:
        return (
          <NormalModeEmail 
            {...commonProps} 
          />
        );
    }
  };

  // Sicherer onClose-Handler
  const handleClose = () => {
    // Verhindere das Schließen während der AutoSend-Verarbeitung
    if (mode === "auto" && isAutoSendProcessing) {
      console.log("Schließen während der AutoSend-Verarbeitung verhindert");
      return;
    }
    
    // Normaler onClose-Aufruf
    onClose();
  };

  // Bestimme die CSS-Klassen basierend auf dem Modus
  const getOverlayClasses = () => {
    const baseClass = "emailAiPopupOverlay";
    return `${baseClass} fullWidth`;
  };

  return (
    <div className={getOverlayClasses()}>
      {/* Globaler Close-Button, immer sichtbar */}
      <button 
        className="emailAiPopupCloseButton emailAiPopupGlobalCloseButton" 
        onClick={handleClose}
        disabled={mode === "auto" && isAutoSendProcessing}
        style={mode === "auto" && isAutoSendProcessing ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
      >
        {mode === "auto" && isAutoSendProcessing ? 
          <span title="Cannot close during processing">×</span> : 
          '×'
        }
      </button>
      
      {/* EmailAiPopup-Content */}
      {renderModeComponent()}
    </div>
  );
};

export default EmailAiPopup;
    

